import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Building2, ChevronRight, Loader2, Search, X } from "lucide-react";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { EmptyCard, ErrorCard, LoadingBlock, PageHeader, Pill } from "../components/jamora-admin/AdminBits";
import { useAdminSession } from "../components/jamora-admin/SessionProvider";
import { MIGRATION_LABELS, adminError, canViewPharmacy, isSessionRequired, pharmacyPath } from "../lib/jamoraAdmin";
import { useAuth } from "../contexts/AuthContext";
import { patchedSearch } from "../lib/searchParams";
import { formatDateTime } from "../lib/tickets";

const STATE_TABS = [["", "Toutes"], ["true", "Actives"], ["false", "Désactivées"]];
const KIND_TABS = [["", "Réelles et démo"], ["false", "Réelles"], ["true", "Démonstration"]];
const ASSIST_TABS = [["", "Assistance : toutes"], ["true", "Assistance activée"], ["false", "Assistance non activée"]];
const ONBOARDING_TABS = [["", "Onboarding : tous"], ["true", "Onboarding terminé"], ["false", "Onboarding à faire"]];
const day = (value) => (value ? formatDateTime(value).slice(0, 10) : "—");

function Tabs({ label, tabs, value, onChange, testid }) {
  return (
    <div className="flex gap-1 flex-wrap" role="tablist" aria-label={label}>
      {tabs.map(([id, text]) => (
        <button key={id || "all"} type="button" role="tab" aria-selected={value === id} onClick={() => onChange(id)} data-testid={`${testid}-${id || "all"}`}
          className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${value === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>{text}</button>
      ))}
    </div>
  );
}

// A value matched exactly by Jamora (region, officine type): applied 300 ms after the last keystroke.
function ExactFilter({ value, onChange, placeholder, label, testid }) {
  const [text, setText] = useState(value);
  useEffect(() => { setText(value); }, [value]);
  useEffect(() => {
    if (text.trim() === value) return undefined;
    const timer = setTimeout(() => onChange(text.trim()), 300);
    return () => clearTimeout(timer);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  return <Input value={text} onChange={(e) => setText(e.target.value)} placeholder={placeholder} aria-label={label} maxLength={60} className="h-9 w-full sm:w-56" data-testid={testid} />;
}

function StatusPills({ p }) {
  return (<span className="inline-flex flex-wrap gap-1"><Pill tone={p.active ? "emerald" : "red"}>{p.active ? "Active" : "Désactivée"}</Pill>{p.demo && <Pill tone="violet">Démo</Pill>}</span>);
}

export default function JamoraAdminPharmacies() {
  const { user } = useAuth();
  const session = useAdminSession();
  const [params, setParams] = useSearchParams();
  const active = params.get("active") || "";
  const demo = params.get("demo") || "";
  const assist = params.get("assist") || "";
  const onboarding = params.get("onboarding") || "";
  const region = params.get("region") || "";
  const kind = params.get("type") || "";
  const search = params.get("q") || "";
  const [searchInput, setSearchInput] = useState(search);
  const [state, setState] = useState({ phase: "loading", items: [], cursor: null, error: "" });
  const seq = useRef(0);
  const canOpenFile = canViewPharmacy(user);

  const update = (changes) => setParams(patchedSearch(changes), { replace: true });
  useEffect(() => {
    const timer = setTimeout(() => { if (searchInput !== search) update({ q: searchInput.trim() }); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const load = useCallback(async (cursor) => {
    const mine = ++seq.current;
    setState((s) => ({ ...s, phase: cursor ? "more" : "loading", error: "", items: cursor ? s.items : [] }));
    try {
      const { data } = await api.get("/jamora-admin/pharmacies", { params: { limit: 50, ...(search ? { search } : {}), ...(active ? { active } : {}), ...(demo ? { demo } : {}), ...(assist ? { assist } : {}), ...(onboarding ? { onboarding } : {}),
        ...(region ? { region } : {}), ...(kind ? { type_officine: kind } : {}), ...(cursor ? { cursor } : {}) } });
      if (mine !== seq.current) return;
      setState((s) => ({ phase: "ready", items: cursor ? [...s.items, ...data.items] : data.items, cursor: data.next_cursor, error: "" }));
      session?.touch();
    } catch (e) {
      if (mine !== seq.current) return;
      if (isSessionRequired(e)) { session?.expire(); return; }
      setState((s) => ({ ...s, phase: "error", error: adminError(e) }));
    }
  }, [search, active, demo, assist, onboarding, region, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(null); return () => { seq.current += 1; }; }, [load]);

  const filtered = !!(search || active || demo || assist || onboarding || region || kind);
  const reset = () => { setSearchInput(""); setParams({}, { replace: true }); };

  return (
    <div className="space-y-4" data-testid="admin-pharmacies">
      <PageHeader title="Pharmacies" description="Toutes les pharmacies de la plateforme : état, région, titulaire et avancement. Un clic ouvre la fiche." />
      <Tabs label="État" tabs={STATE_TABS} value={active} onChange={(v) => update({ active: v })} testid="pharmacies-active" />
      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Nom, titulaire, région…" className="pl-9 h-9" aria-label="Rechercher une pharmacie" data-testid="pharmacies-search" />
        </div>
        <Tabs label="Type" tabs={KIND_TABS} value={demo} onChange={(v) => update({ demo: v })} testid="pharmacies-demo" />
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Tabs label="Assistance à distance" tabs={ASSIST_TABS} value={assist} onChange={(v) => update({ assist: v })} testid="pharmacies-assist" />
        <Tabs label="Onboarding" tabs={ONBOARDING_TABS} value={onboarding} onChange={(v) => update({ onboarding: v })} testid="pharmacies-onboarding" />
        <ExactFilter value={region} placeholder="Région (valeur exacte)" label="Filtrer par région" onChange={(v) => update({ region: v })} testid="pharmacies-region" />
        <ExactFilter value={kind} placeholder="Type d'officine (valeur exacte)" label="Filtrer par type d'officine" onChange={(v) => update({ type: v })} testid="pharmacies-type" />
      </div>

      {state.phase === "loading" && <LoadingBlock rows={4} />}
      {state.phase === "error" && <ErrorCard message={state.error} onRetry={() => load(null)} />}
      {state.phase === "ready" && !state.items.length && (
        <EmptyCard icon={Building2} testid="pharmacies-empty" title={filtered ? "Aucune pharmacie ne correspond" : "Aucune pharmacie pour le moment"}
          hint={filtered ? "Essayez un autre nom ou retirez un filtre." : "Les pharmacies créées dans Jamora apparaîtront ici."}>
          {filtered && <Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button>}
        </EmptyCard>
      )}
      {(state.phase === "ready" || state.phase === "more") && state.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="pharmacies-table">
            <Table>
              <TableHeader><TableRow><TableHead>Pharmacie</TableHead><TableHead>État</TableHead><TableHead className="hidden lg:table-cell">Région</TableHead><TableHead>Titulaire</TableHead><TableHead className="hidden lg:table-cell">Onboarding</TableHead><TableHead className="hidden xl:table-cell">Migration</TableHead><TableHead>Créée le</TableHead></TableRow></TableHeader>
              <TableBody>
                {state.items.map((p) => (
                  <TableRow key={p.schema} data-testid={`pharmacy-row-${p.schema}`}>
                    <TableCell><div className="font-medium text-slate-900">{canOpenFile ? <Link className="text-jamora hover:underline" to={pharmacyPath(p.schema)}>{p.name}</Link> : p.name}</div><div className="text-xs text-slate-500">{p.primary_domain || p.schema}</div></TableCell>
                    <TableCell><StatusPills p={p} /></TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{p.region || "—"}</TableCell>
                    <TableCell className="text-sm">{p.responsable || "—"}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{p.onboarding_completed ? "Terminé" : "À faire"}</TableCell>
                    <TableCell className="hidden xl:table-cell text-sm">{MIGRATION_LABELS[p.migration_status] || p.migration_status}</TableCell>
                    <TableCell className="text-sm whitespace-nowrap">{day(p.created_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="pharmacies-cards">
            {state.items.map((p) => {
              const body = (
                <>
                  <div className="min-w-0 flex-1 space-y-0.5">
                    <div className="font-medium text-slate-900">{p.name}</div>
                    <div className="text-xs text-slate-500">{[p.region, p.responsable].filter(Boolean).join(" · ") || p.schema}</div>
                    <StatusPills p={p} />
                  </div>
                  {canOpenFile && <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" aria-hidden="true" />}
                </>
              );
              return canOpenFile
                ? <Link key={p.schema} to={pharmacyPath(p.schema)} className="flex items-start gap-3 px-4 py-3" data-testid={`pharmacy-card-${p.schema}`}>{body}</Link>
                : <div key={p.schema} className="flex items-start gap-3 px-4 py-3" data-testid={`pharmacy-card-${p.schema}`}>{body}</div>;
            })}
          </Card>
          <div className="flex items-center justify-between gap-3">
            <p className="text-xs text-slate-500" data-testid="pharmacies-count">{state.items.length} pharmacie{state.items.length > 1 ? "s" : ""} affichée{state.items.length > 1 ? "s" : ""}{state.cursor ? " (il y en a d'autres)" : ""}</p>
            {state.cursor && <Button variant="outline" size="sm" onClick={() => load(state.cursor)} disabled={state.phase === "more"} data-testid="pharmacies-more">{state.phase === "more" && <Loader2 size={14} className="mr-1.5 animate-spin" />}Voir plus</Button>}
          </div>
        </>
      )}
    </div>
  );
}
